/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx } from '@compiled/react';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import { MenuButtonItem, MenuLinkItem } from '@atlassian/navigation-system';

import { MenuItem } from '../../../types';

const itemWrapStyles = xcss({
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
	width: '246px',
});

export default function MenuContentItem({
	item,
	onCloseMenu,
}: {
	item: MenuItem;
	onCloseMenu: () => void;
}) {
	const { onClick, onClickCloseMenu = true, children, spotlightTarget, ...itemProps } = item;

	const buttonClick = useCallback(
		(
			event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
			analyticsEvent: UIAnalyticsEvent,
		) => {
			if (onClickCloseMenu) {
				onCloseMenu();
			}

			onClick?.(event, analyticsEvent);
		},
		[onClick, onClickCloseMenu, onCloseMenu],
	);

	const renderButton = useCallback(
		(visualContentRef?: React.Ref<HTMLDivElement>) => (
			<Box xcss={itemWrapStyles}>
				{'href' in itemProps ? (
					<MenuLinkItem
						{...itemProps}
						onClick={buttonClick}
						children={children}
						{...(visualContentRef ? { visualContentRef } : {})}
					/>
				) : (
					<MenuButtonItem
						{...itemProps}
						onClick={buttonClick}
						children={children}
						{...(visualContentRef ? { visualContentRef } : {})}
					/>
				)}
			</Box>
		),
		[buttonClick, children, itemProps],
	);

	return spotlightTarget ? (
		<SpotlightTarget name={spotlightTarget}>
			{({ targetRef }) => renderButton(targetRef)}
		</SpotlightTarget>
	) : (
		renderButton()
	);
}
